"use client";

import {
  BENEFIT_PROVIDER_NAMES,
  BENEFIT_VARIANTS,
  BenefitProviderLogo,
  parseBenefitProvider,
  type BenefitProvider,
} from "@fitro/customer-ui/BenefitProvider";
import { useTranslation } from "../../../hooks/useTranslation";

/** One card price, naming every card that gets it (grouped by the API). */
export type BenefitCardPriceLine = {
  cardName: string;
  priceMinor: number;
  cards: { provider: string; variant: string | null }[];
};

/** Card price first, with the accepted brands vertically centered beside it. */
export function CardPriceLine({
  line,
  price,
}: {
  line: BenefitCardPriceLine;
  price: string;
}) {
  const { t } = useTranslation("landing");
  const providers = [
    ...new Set(line.cards.map((card) => parseBenefitProvider(card.provider))),
  ];
  return (
    <p
      className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5 text-[11px] leading-4 text-[var(--landing-neutral-500)] xl:justify-start"
    >
      <span
        className="whitespace-nowrap text-[26px] font-semibold leading-7 tabular-nums text-[var(--landing-neutral-900)] sm:text-[28px]"
      >
        {price}
      </span>
      {providers.map((provider) =>
        provider ? (
          <span
            key={provider}
            className="inline-flex items-center gap-1.5 text-[13px]"
          >
            <span className="text-sm font-normal text-slate-600">
              {t("publicEntry.pricing.classes.withCard")}
            </span>
            <BenefitProviderLogo provider={provider} />
            {variantNames(provider, line.cards)}
          </span>
        ) : null,
      )}
    </p>
  );
}

/** The brand's variant names, or nothing when the whole brand gets the price. */
function variantNames(
  provider: BenefitProvider,
  cards: { provider: string; variant: string | null }[],
): string | null {
  const brandCards = cards.filter((card) => card.provider === provider);
  if (brandCards.some((card) => !card.variant)) return null;
  const names = brandCards
    .map(
      (card) =>
        BENEFIT_VARIANTS[provider].find(
          (variant) => variant.code === card.variant,
        )?.name,
    )
    .filter((name) => name && name !== BENEFIT_PROVIDER_NAMES[provider]);
  return names.length > 0 ? names.join(", ") : null;
}
